import { useState, useEffect } from "react";
import { Heart, Shield, Zap, Sparkles, Leaf, Moon, Activity, Sun, Star, Clock, MapPin, Phone, ArrowRight, CheckCircle, HelpCircle, PartyPopper } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import MassageBenefitsPlayer from "@/components/MassageBenefitsPlayer";

const HERO_IMAGE = "https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=1200&q=80";
const LACRESHA_IMAGE = "https://customer-assets.emergentagent.com/job_f651e4ab-7ee9-48a6-ba28-4c2cc6db2347/artifacts/8b80j9be_IMG_5229.jpeg";
const MASSAGE_IMAGE = "https://images.unsplash.com/photo-1649751295468-953038600bef?w=800&q=80";

const BOOKING_URL = "https://www.massagebook.com/biz/lacresharamos";

// Fun Facts - Rotates weekly
const funFacts = [
  {
    question: "How much can regular massage reduce cortisol (stress hormone) levels?",
    options: ["Up to 10%", "Up to 30%", "Up to 50%"],
    correctIndex: 2,
    funFact: "Regular massage can reduce cortisol levels by up to 50%! That's why you feel so relaxed after a session. Your body literally de-stresses at the cellular level!"
  },
  {
    question: "How many muscles does a full-body massage typically address?",
    options: ["About 50", "Over 200", "Over 600"],
    correctIndex: 2,
    funFact: "A full-body massage can address over 600 muscles! The human body has approximately 640 muscles, and a skilled therapist knows how to help them all relax."
  },
  {
    question: "What happens to your blood circulation during a massage?",
    options: ["Stays the same", "Increases by 25%", "Can double"],
    correctIndex: 2,
    funFact: "Blood circulation can actually DOUBLE during a massage! This increased flow delivers more oxygen and nutrients to your tissues while flushing out toxins."
  },
  {
    question: "How long do the benefits of a single massage session typically last?",
    options: ["A few hours", "Up to a week", "Up to a month"],
    correctIndex: 1,
    funFact: "The benefits of a single massage can last up to a week! Regular sessions compound these effects, leading to lasting improvements in your overall well-being."
  },
  {
    question: "What percentage of people report better sleep after massage therapy?",
    options: ["About 40%", "About 60%", "Over 75%"],
    correctIndex: 2,
    funFact: "Over 75% of people report improved sleep quality after massage! The relaxation response triggers your parasympathetic nervous system, preparing your body for deep, restorative rest."
  },
  {
    question: "How much can massage therapy reduce tension headache frequency?",
    options: ["By 20%", "By 50%", "By up to 80%"],
    correctIndex: 2,
    funFact: "Studies show massage can reduce tension headache frequency by up to 80%! By releasing muscle tension in the neck, shoulders, and head, many chronic headache sufferers find lasting relief."
  },
  {
    question: "What 'feel-good' chemicals does massage release in your brain?",
    options: ["Only endorphins", "Endorphins & serotonin", "Endorphins, serotonin & dopamine"],
    correctIndex: 2,
    funFact: "Massage releases the happiness trifecta: endorphins, serotonin, AND dopamine! It's like a natural mood boost that leaves you feeling euphoric and content."
  },
  {
    question: "How does massage affect your immune system?",
    options: ["No effect", "Slightly boosts it", "Significantly increases white blood cells"],
    correctIndex: 2,
    funFact: "Massage significantly increases white blood cell count! Research shows just one 45-minute massage can boost your lymphocytes - the cells that help fight off illness."
  }
];

// Get current week of the year to determine which fact to show
const getWeekNumber = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  const diff = now - start;
  const oneWeek = 1000 * 60 * 60 * 24 * 7;
  return Math.floor(diff / oneWeek);
};

const FunFactBanner = () => {
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const weekNum = getWeekNumber();
  const currentFact = funFacts[weekNum % funFacts.length];

  const handleAnswer = (index) => {
    setSelectedAnswer(index);
    setShowResult(true);
  };

  const resetQuiz = () => {
    setSelectedAnswer(null);
    setShowResult(false);
  };

  return (
    <section className="py-12 bg-gradient-to-r from-[#0F172A] via-[#1E293B] to-[#0F172A] relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-[#D4AF37] rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-[#D4AF37] rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>
      
      <div className="max-w-4xl mx-auto px-6 relative z-10">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 bg-[#D4AF37]/20 border border-[#D4AF37]/40 px-4 py-2 rounded-full mb-4">
            <HelpCircle size={18} className="text-[#D4AF37]" />
            <span className="text-[#D4AF37] font-semibold text-sm">Weekly Fun Fact Quiz</span>
          </div>
        </div>

        {!showResult ? (
          <div className="animate-fade-in">
            <h3 className="text-2xl md:text-3xl font-bold text-white text-center mb-8">
              {currentFact.question}
            </h3>
            
            <div className="grid md:grid-cols-3 gap-4 max-w-2xl mx-auto">
              {currentFact.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleAnswer(index)}
                  className="p-4 bg-white/10 border-2 border-white/20 hover:border-[#D4AF37] hover:bg-[#D4AF37]/20 text-white font-medium rounded-lg transition-all duration-300 hover:scale-105"
                  data-testid={`quiz-option-${index}`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="animate-scale-in text-center">
            {selectedAnswer === currentFact.correctIndex ? (
              <div className="mb-6">
                <PartyPopper size={48} className="text-[#D4AF37] mx-auto mb-4 animate-bounce" />
                <p className="text-2xl font-bold text-[#D4AF37] mb-2">You Got It! 🎉</p>
              </div>
            ) : (
              <div className="mb-6">
                <p className="text-2xl font-bold text-white mb-2">Good Try!</p>
                <p className="text-slate-400">The correct answer was: <span className="text-[#D4AF37] font-semibold">{currentFact.options[currentFact.correctIndex]}</span></p>
              </div>
            )}
            
            <div className="bg-white/10 backdrop-blur border border-[#D4AF37]/30 rounded-lg p-6 mb-6">
              <p className="text-white text-lg leading-relaxed">
                {currentFact.funFact}
              </p>
            </div>
            
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                onClick={resetQuiz}
                variant="outline" 
                className="border-white/30 text-white hover:bg-white/10"
              >
                Try Again
              </Button>
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#0F172A] font-semibold">
                  Experience These Benefits - Book Now!
                </Button>
              </a>
            </div>
          </div>
        )}
        
        <p className="text-center text-slate-500 text-xs mt-6">
          New fun fact every week! Come back to test your wellness knowledge.
        </p>
      </div>
    </section>
  );
};

const services = [
  {
    name: "Elegant Escape Massage",
    description: "Classic relaxation massage using long, flowing strokes to melt away tension.",
    duration: "60 min",
    price: "$80+",
    icon: Leaf,
    popular: true
  },
  {
    name: "Deep Tissue",
    description: "Targeted pressure to release chronic muscle tension and knots.",
    duration: "60 min",
    price: "$85+",
    icon: Zap,
    popular: true
  },
  {
    name: "Massage Madness",
    description: "A blend of sports massage and therapeutic massage focusing on areas of pain, guided stretch, and releasing tension in sore muscles.",
    duration: "75 min",
    price: "$110+",
    icon: Activity,
    popular: false
  },
  {
    name: "Spa Sampler",
    description: "Great for beginners! Experience spa treatments while relaxing and boosting your immune system. Includes 30 min massage and 30 min facial with foot scrub and essential oils.",
    duration: "60 min",
    price: "$110+",
    icon: Sparkles,
    popular: false
  },
  {
    name: "Sports Massage",
    description: "Enhance performance and speed up recovery for active lifestyles.",
    duration: "60 min",
    price: "$80+",
    icon: Zap,
    popular: false
  },
  {
    name: "Prenatal Massage",
    description: "Gentle care designed specifically for expectant mothers.",
    duration: "60 min",
    price: "$80+",
    icon: Heart,
    popular: false
  }
];

const testimonials = [
  { name: "Sarah M.", text: "Best massage I've ever had! Lacresha's expertise is unmatched.", rating: 5 },
  { name: "Michael T.", text: "My back pain is finally gone after years of suffering. Highly recommend!", rating: 5 },
  { name: "Jennifer L.", text: "The hot stone therapy was absolutely amazing. I felt like a new person!", rating: 5 },
  { name: "David R.", text: "Lacresha has magic hands! I've been coming monthly for 2 years and wouldn't go anywhere else.", rating: 5 },
  { name: "Amanda K.", text: "Finally found relief from my migraines. The deep tissue work is life-changing!", rating: 5 },
  { name: "Robert S.", text: "As an athlete, recovery is everything. Lacresha keeps me performing at my best.", rating: 5 }
];

const benefits = [
  "Reduce stress & anxiety",
  "Relieve chronic pain",
  "Improve circulation",
  "Better sleep quality",
  "Boost immunity",
  "Increase flexibility"
];

export const ServicesPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="services-page">
      <Navbar />
      
      {/* Hero Section - Full Impact */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A]/95 via-[#0F172A]/80 to-transparent" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32 pt-40">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-[#D4AF37]/20 border border-[#D4AF37]/40 px-4 py-2 rounded-full mb-6 animate-fade-in">
              <Star size={16} className="text-[#D4AF37] fill-[#D4AF37]" />
              <span className="text-[#D4AF37] text-sm font-medium">Daytona Beach's Premier Massage Therapist</span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight mb-6 animate-fade-in animation-delay-100">
              Your Body<br />
              <span className="text-[#D4AF37]">Deserves</span> This
            </h1>
            
            <p className="text-xl text-slate-300 leading-relaxed mb-8 animate-fade-in animation-delay-200">
              Experience the healing touch of 16 years of expertise. Relief from pain, 
              stress, and tension is just one appointment away.
            </p>
            
            <div className="flex flex-wrap gap-4 animate-fade-in animation-delay-300">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                <Button className="h-14 px-10 bg-[#D4AF37] hover:bg-[#C5A028] text-[#0F172A] text-lg font-semibold rounded-none transition-all hover:scale-105" data-testid="hero-book-btn">
                  BOOK YOUR MASSAGE
                  <ArrowRight size={20} className="ml-2" />
                </Button>
              </a>
              <a href="tel:386-286-4481">
                <Button variant="outline" className="h-14 px-8 border-2 border-white text-white hover:bg-white/10 text-lg rounded-none">
                  <Phone size={20} className="mr-2" />
                  Call Now
                </Button>
              </a>
            </div>
            
            <div className="flex items-center gap-6 mt-10 animate-fade-in animation-delay-400">
              <div className="flex -space-x-3">
                {[1,2,3,4].map(i => (
                  <div key={i} className="w-10 h-10 rounded-full bg-[#D4AF37] border-2 border-[#0F172A] flex items-center justify-center">
                    <Star size={14} className="text-[#0F172A] fill-[#0F172A]" />
                  </div>
                ))}
              </div>
              <div>
                <p className="text-white font-semibold">500+ Happy Clients</p>
                <p className="text-slate-400 text-sm">5-Star Reviews</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Urgency Banner */}
      <section className="bg-[#D4AF37] py-4">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-[#0F172A] font-semibold">
            <Clock size={18} className="inline mr-2" />
            Limited Availability This Week — Book Now to Secure Your Spot!
          </p>
        </div>
      </section>

      {/* Fun Fact Quiz Banner */}
      <FunFactBanner />

      {/* Monthly Massage Benefits Player */}
      <MassageBenefitsPlayer />

      {/* Quick Benefits */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {benefits.map((benefit, index) => (
              <div 
                key={benefit}
                className="flex items-center gap-2 p-4 bg-slate-50 border border-slate-100 animate-fade-in"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <CheckCircle size={18} className="text-[#D4AF37] flex-shrink-0" />
                <span className="text-sm font-medium text-[#0F172A]">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-slate-50" data-testid="services-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-[#D4AF37] font-medium tracking-widest uppercase mb-4">
              Transform Your Well-Being
            </p>
            <h2 className="section-heading text-4xl md:text-5xl font-bold mb-6">
              Choose Your Perfect Massage
            </h2>
            <p className="text-slate-600 text-lg">
              Every session is tailored to your unique needs. Which one calls to you?
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div 
                  key={service.name}
                  className="relative bg-white p-8 border border-slate-200 hover:border-[#D4AF37] hover:shadow-xl transition-all duration-300 group animate-fade-in"
                  style={{ animationDelay: `${index * 100}ms` }}
                  data-testid={`service-card-${index}`}
                >
                  {service.popular && (
                    <div className="absolute -top-3 right-6 bg-[#D4AF37] text-[#0F172A] text-xs font-bold px-3 py-1">
                      MOST POPULAR
                    </div>
                  )}
                  
                  <div className="w-14 h-14 bg-slate-100 group-hover:bg-[#D4AF37] flex items-center justify-center mb-6 transition-colors">
                    <Icon size={28} className="text-[#D4AF37] group-hover:text-[#0F172A] transition-colors" />
                  </div>
                  
                  <h3 className="text-xl font-bold text-[#0F172A] mb-3">{service.name}</h3>
                  <p className="text-slate-600 mb-6 leading-relaxed">{service.description}</p>
                  
                  <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                    <div>
                      <span className="text-2xl font-bold text-[#0F172A]">{service.price}</span>
                      <span className="text-slate-500 text-sm ml-2">/ {service.duration}</span>
                    </div>
                    <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                      <Button size="sm" className="bg-[#0F172A] hover:bg-[#D4AF37] hover:text-[#0F172A] transition-colors">
                        Book
                      </Button>
                    </a>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
              <Button className="h-14 px-12 bg-[#D4AF37] hover:bg-[#C5A028] text-[#0F172A] text-lg font-semibold rounded-none">
                VIEW ALL SERVICES & BOOK NOW
                <ArrowRight size={20} className="ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Meet Lacresha */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img 
                src={LACRESHA_IMAGE}
                alt="Lacresha Ramos, LMT"
                className="w-full h-[600px] object-cover object-top"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#0F172A] to-transparent p-8">
                <p className="text-[#D4AF37] font-semibold">Licensed Massage Therapist</p>
                <p className="text-white text-2xl font-bold">Lacresha Ramos, LMT</p>
              </div>
            </div>
            
            <div>
              <p className="text-[#D4AF37] font-medium tracking-widest uppercase mb-4">
                Your Therapist
              </p>
              <h2 className="section-heading text-4xl font-bold mb-6">
                16 Years of Healing Hands
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                When you book with Lacresha, you're not just getting a massage — you're getting 
                over a decade and a half of expertise, intuition, and genuine care for your well-being.
              </p>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                Hundreds of clients trust Lacresha to relieve their pain, reduce their stress, 
                and help them feel like themselves again. You deserve that same transformation.
              </p>
              
              <div className="grid grid-cols-3 gap-6 mb-8">
                <div className="text-center p-4 bg-slate-50">
                  <p className="text-3xl font-bold text-[#D4AF37]">16+</p>
                  <p className="text-sm text-slate-600">Years Experience</p>
                </div>
                <div className="text-center p-4 bg-slate-50">
                  <p className="text-3xl font-bold text-[#D4AF37]">500+</p>
                  <p className="text-sm text-slate-600">Happy Clients</p>
                </div>
                <div className="text-center p-4 bg-slate-50">
                  <p className="text-3xl font-bold text-[#D4AF37]">5.0</p>
                  <p className="text-sm text-slate-600">Star Rating</p>
                </div>
              </div>
              
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                <Button className="h-14 px-10 bg-[#0F172A] hover:bg-[#D4AF37] hover:text-[#0F172A] text-white text-lg font-semibold rounded-none transition-colors">
                  BOOK WITH LACRESHA
                  <ArrowRight size={20} className="ml-2" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="text-[#D4AF37] font-medium tracking-widest uppercase mb-4">
              Real Results
            </p>
            <h2 className="text-4xl font-bold text-white mb-4 font-display">
              What Clients Are Saying
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div 
                key={testimonial.name}
                className="bg-white/5 backdrop-blur border border-white/10 p-6 animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="flex gap-1 mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={16} className="text-[#D4AF37] fill-[#D4AF37]" />
                  ))}
                </div>
                <p className="text-white text-base mb-4 italic">"{testimonial.text}"</p>
                <p className="text-[#D4AF37] font-semibold">{testimonial.name}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/testimonials">
              <Button variant="outline" className="h-12 px-8 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0F172A]">
                Read More Reviews
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Location & Contact */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#D4AF37] font-medium tracking-widest uppercase mb-4">
                Visit Us
              </p>
              <h2 className="section-heading text-4xl font-bold mb-6">
                Conveniently Located in Daytona Beach
              </h2>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4">
                  <MapPin size={24} className="text-[#D4AF37] flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-[#0F172A]">211 E. International Speedway Blvd # 114</p>
                    <p className="text-slate-600">Daytona Beach, FL 32118</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Phone size={24} className="text-[#D4AF37] flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-[#0F172A]">386-286-4481</p>
                    <p className="text-slate-600">Call or text to book</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Clock size={24} className="text-[#D4AF37] flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-[#0F172A]">Mon-Fri: 9am-7pm</p>
                    <p className="text-slate-600">Sat: 10am-5pm | Sun: By Appointment</p>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-4">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                  <Button className="h-12 px-8 bg-[#D4AF37] hover:bg-[#C5A028] text-[#0F172A] font-semibold rounded-none">
                    BOOK ONLINE
                  </Button>
                </a>
                <a href="tel:386-286-4481">
                  <Button variant="outline" className="h-12 px-8 border-[#0F172A] text-[#0F172A] rounded-none">
                    <Phone size={18} className="mr-2" />
                    CALL NOW
                  </Button>
                </a>
              </div>
            </div>
            
            <div className="bg-[#0F172A] p-10 text-center">
              <h3 className="text-2xl font-bold text-white mb-4">Ready to Feel Amazing?</h3>
              <p className="text-slate-400 mb-8">
                Don't wait another day to invest in yourself. Your body will thank you.
              </p>
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                <Button className="h-14 px-12 bg-[#D4AF37] hover:bg-[#C5A028] text-[#0F172A] text-lg font-bold rounded-none w-full">
                  BOOK YOUR MASSAGE NOW
                  <ArrowRight size={20} className="ml-2" />
                </Button>
              </a>
              <p className="text-slate-500 text-sm mt-4">
                Online booking available 24/7
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
